import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef,
    EventEmitter, HostBinding, Input, Output, ViewChild,
} from '@angular/core';
import { DxcIconComponent } from '../icon/dxc-icon.component';
import { DxcIconName } from '../dxc.types';

/**
 * Arrastrar y soltar un archivo.
 *
 * `.dxc-dropzone` existía como clase desde el principio, pero solo era una caja
 * discontinua: no escuchaba `drop`, así que la superficie *parecía* que aceptaba
 * un archivo arrastrado y no lo aceptaba. Cada vista que la usaba acababa
 * poniendo un `<input type="file">` escondido y un `click()` a mano, y ninguna
 * llegó a implementar el arrastre. Eso es exactamente lo que la regla llama
 * aplicar una piel en vez de migrar: la caja correcta con el comportamiento de
 * otra cosa.
 *
 * Decisiones:
 *
 * - **El componente ES el botón.** Una zona de arrastre tiene que poder recibir
 *   foco y responder a Enter y Espacio, o queda fuera del alcance del teclado.
 * - **`dragenter`/`dragleave` se cuentan, no se alternan.** Al pasar el puntero
 *   sobre un hijo el navegador dispara `dragleave` del padre; alternando un
 *   booleano el resaltado parpadea. Con un contador de profundidad no.
 * - **El icono es del dominio**, no una flecha genérica: `soltar-archivo` para
 *   el hueco vacío, y quien lo consume puede pasar el suyo (`prueba-entrega`,
 *   `foto-entrega`, `documento-adjunto`).
 * - **`accept` se comprueba también al soltar.** El diálogo del sistema filtra,
 *   pero arrastrar no: sin esta comprobación se cuela cualquier extensión.
 * - **Dos densidades.** `--compact` para un hueco dentro de una lista estrecha;
 *   la normal para un diálogo, donde hay sitio para explicar qué se espera.
 */
@Component({
    standalone: true,
    selector: 'dxc-upload',
    imports: [DxcIconComponent],
    templateUrl: './dxc-upload.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DxcUploadComponent {

    /** Rótulo principal. En compacto es lo único que se lee. */
    @Input() label = 'Arrastra un archivo o pulsa para elegirlo';

    /** Segunda línea: qué formatos, qué tamaño, qué se espera. */
    @Input() hint = '';

    /** Filtro del diálogo del sistema. Se aplica también al soltar. */
    @Input() accept = '';

    @Input() multiple = false;

    @Input() disabled = false;

    /** Una densidad más apretada para un hueco dentro de una lista. */
    @Input() compact = false;

    @Input() icon: DxcIconName = 'soltar-archivo';

    @Input() ariaLabel = '';

    /**
     * Tamaño máximo en bytes; 0 no limita. Existe porque el límite vivía solo
     * en el `hint`: los documentos de la empresa decían «máx. 2 MB» y nada lo
     * comprobaba, así que un archivo de 40 MB se subía igual.
     */
    @Input() maxSize = 0;

    @Output() filesSelected = new EventEmitter<File[]>();

    /** Se emite cuando lo elegido o soltado no pasa `accept` o `maxSize`. */
    @Output() rejected = new EventEmitter<File[]>();

    @ViewChild('input') private inputRef?: ElementRef<HTMLInputElement>;

    @HostBinding('class.dxc-upload-host') readonly hostClass = true;

    isOver = false;

    /**
     * Profundidad de arrastre. El navegador dispara `dragleave` del contenedor
     * cada vez que el puntero entra en un hijo; alternando un booleano el
     * resaltado parpadea mientras se mueve por encima.
     */
    #depth = 0;

    constructor(private cdr: ChangeDetectorRef) { }

    open(): void {
        if (this.disabled) return;
        this.inputRef?.nativeElement.click();
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.open();
    }

    onDragEnter(event: DragEvent): void {
        if (this.disabled) return;
        event.preventDefault();
        this.#depth += 1;
        if (!this.isOver) {
            this.isOver = true;
            this.cdr.markForCheck();
        }
    }

    onDragOver(event: DragEvent): void {
        if (this.disabled) return;
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    }

    onDragLeave(event: DragEvent): void {
        if (this.disabled) return;
        event.preventDefault();
        this.#depth = Math.max(0, this.#depth - 1);
        if (this.#depth === 0 && this.isOver) {
            this.isOver = false;
            this.cdr.markForCheck();
        }
    }

    onDrop(event: DragEvent): void {
        if (this.disabled) return;
        event.preventDefault();
        this.#depth = 0;
        this.isOver = false;
        this.handleFiles([...(event.dataTransfer?.files ?? [])]);
    }

    /**
     * La misma puerta que el diálogo del sistema y el soltar, abierta para
     * quien envuelve la zona en una superficie mayor —`<dxc-attachments>` hace
     * del panel entero la zona de soltar—. Se expone el método y no se copia
     * el filtro: `accept` comprobado dos veces son dos contratos que se
     * separan el día que uno de los dos cambie.
     */
    handleFiles(files: File[]): void {
        if (this.disabled) return;
        this.emit(files);
    }

    onPicked(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.emit([...(input.files ?? [])]);
        // Vaciar permite volver a elegir el mismo archivo: sin esto el segundo
        // intento no dispara `change` y parece que la subida se ha ignorado.
        input.value = '';
    }

    private emit(files: File[]): void {
        if (!files.length) return;

        const picked = this.multiple ? files : files.slice(0, 1);
        const fits = (file: File) => this.matchesAccept(file) && (!this.maxSize || file.size <= this.maxSize);
        const accepted = picked.filter(fits);
        const refused = picked.filter(file => !fits(file));

        if (refused.length) this.rejected.emit(refused);
        if (accepted.length) this.filesSelected.emit(accepted);

        this.cdr.markForCheck();
    }

    /** Mismo contrato que el atributo `accept`: extensión, mime, o `tipo/*`. */
    private matchesAccept(file: File): boolean {
        const accept = this.accept?.trim();
        if (!accept) return true;

        const name = file.name.toLowerCase();
        const type = (file.type || '').toLowerCase();

        return accept.split(',').map(rule => rule.trim().toLowerCase()).filter(Boolean).some(rule => {
            if (rule.startsWith('.')) return name.endsWith(rule);
            if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
            return type === rule;
        });
    }
}
